// components/TrialExpiredModal.js
// Clean, professional trial expired modal
// Last updated: November 28, 2025

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const HEADLINES = [
  "Your Free Trial Has Ended",
  "Time to Make It Official",
  "Your Trial Period is Over",
  "Ready to Go Pro?",
  "Trial Complete"
];

const SUBHEADLINES = [
  "You've experienced what WhyDidItDie can do. Now let's keep the momentum going.",
  "Your marketing deserves better than guesswork. Upgrade to keep the insights flowing.",
  "Don't let your competitors get ahead. Continue optimizing with full access.",
  "You've seen the value. Now unlock unlimited access to all features.",
  "Great marketers invest in great tools. Your trial showed you why."
];

const CTAS = [
  "Upgrade to Pro",
  "Continue with Pro",
  "Unlock Full Access",
  "Get Pro Access",
  "Start Pro Plan"
];

const FUN_FACTS = [
  "The average marketer wastes 26% of their budget on underperforming campaigns.",
  "Companies using marketing analytics are 2.8x more likely to exceed revenue goals.",
  "Pro users save an average of 12 hours per week on campaign analysis.",
  "Data-driven marketers are 6x more likely to be profitable year-over-year.",
  "WhyDidItDie users report finding 23% more budget waste in their first month.",
];

const FEATURES = [
  { icon: '📊', name: 'Campaign Detective' },
  { icon: '🔍', name: 'Waste Detective' },
  { icon: '🤖', name: 'AI Content Engine' },
  { icon: '🌍', name: 'Market Intelligence' },
  { icon: '🎯', name: 'Platform Intelligence' },
  { icon: '📝', name: 'Decision Tracker' },
];

const TrialExpiredModal = ({ daysOverdue = 0 }) => {
  const navigate = useNavigate();
  const [headlineIndex] = useState(() => Math.floor(Math.random() * HEADLINES.length));
  const [subheadlineIndex] = useState(() => Math.floor(Math.random() * SUBHEADLINES.length));
  const [ctaIndex] = useState(() => Math.floor(Math.random() * CTAS.length));
  const [funFactIndex, setFunFactIndex] = useState(() => Math.floor(Math.random() * FUN_FACTS.length));
  const [funFactFading, setFunFactFading] = useState(false);

  // Rotate fun facts every 10 seconds with fade
  useEffect(() => {
    const interval = setInterval(() => {
      setFunFactFading(true);
      setTimeout(() => {
        setFunFactIndex((prev) => (prev + 1) % FUN_FACTS.length);
        setFunFactFading(false);
      }, 500);
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  // Prevent interaction with page behind
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleUpgrade = () => {
    navigate('/pricing');
  };

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-gray-900/98 backdrop-blur-sm z-[10000]" />
      
      {/* Modal container */}
      <div className="fixed inset-0 z-[10001] overflow-y-auto">
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            
            {/* Card */}
            <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6 sm:p-8 shadow-2xl">
              
              {/* Status Badge */}
              <div className="flex justify-center mb-5">
                <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-500/30 text-orange-400 px-4 py-1.5 rounded-full text-sm font-medium">
                  <span className="w-2 h-2 bg-orange-400 rounded-full animate-pulse"></span>
                  Trial Ended
                </div>
              </div>

              {/* Headline */}
              <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-3">
                {HEADLINES[headlineIndex]}
              </h2>
              
              {/* Days overdue */}
              {daysOverdue > 0 && (
                <p className="text-center text-orange-400 text-sm mb-4">
                  Your trial ended {daysOverdue} day{daysOverdue !== 1 ? 's' : ''} ago
                </p>
              )}

              {/* Subtitle */}
              <p className="text-gray-400 text-center mb-6 text-sm sm:text-base">
                {SUBHEADLINES[subheadlineIndex]}
              </p>

              {/* Features Grid */}
              <div className="bg-gray-900/50 border border-gray-700 rounded-xl p-4 mb-5">
                <p className="text-xs text-gray-500 font-medium mb-3 uppercase tracking-wide">
                  What you'll get with Pro:
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {FEATURES.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className="text-lg">{feature.icon}</span>
                      <span className="text-gray-300 text-sm">{feature.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Fun Fact */}
              <div className={`bg-blue-500/10 border border-blue-500/20 rounded-lg px-4 py-3 mb-5 transition-opacity duration-500 ${funFactFading ? 'opacity-0' : 'opacity-100'}`}>
                <p className="text-blue-300 text-xs sm:text-sm text-center">
                  💡 {FUN_FACTS[funFactIndex]}
                </p>
              </div>

              {/* Pricing */}
              <div className="text-center mb-6">
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-bold text-white">$15</span>
                  <span className="text-gray-400">/month</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  or $165/year <span className="text-green-400">(save $15)</span>
                </p>
              </div>

              {/* CTA Button */}
              <button
                onClick={handleUpgrade}
                className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3.5 px-6 rounded-xl font-semibold text-base hover:from-blue-600 hover:to-blue-700 transition-all shadow-lg hover:shadow-blue-500/25"
              >
                {CTAS[ctaIndex]} →
              </button>

              {/* Trust signals */}
              <div className="flex items-center justify-center gap-4 mt-5 text-xs text-gray-500">
                <span>🔒 Secure payment</span>
                <span>•</span>
                <span>Cancel anytime</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TrialExpiredModal;